import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import SimpleRichTextEditor, { parseRichTextContent } from '../ui/rich-text-editor';
import { Upload, Download, Loader2, Image as ImageIcon, X, ArrowLeft, Info, Type } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PLATFORMS = [
  // Instagram
  { value: 'instagram-post-square', label: '📸 Instagram - Square Post (1080×1080)' },
  { value: 'instagram-post-portrait', label: '📸 Instagram - Portrait Post (1080×1350)' },
  { value: 'instagram-post-landscape', label: '📸 Instagram - Landscape Post (1920×1080)' },
  { value: 'instagram-story', label: '📸 Instagram - Story (1080×1920)' },
  { value: 'instagram-reel', label: '📸 Instagram - Reel (1080×1920)' },
  { value: 'instagram-reel-cover', label: '📸 Instagram - Reel Cover (1080×1920)' },
  
  // Facebook
  { value: 'facebook-cover-personal', label: '👥 Facebook - Personal Cover (851×315)' },
  { value: 'facebook-cover-business', label: '👥 Facebook - Business Cover (820×312)' },
  { value: 'facebook-post-landscape', label: '👥 Facebook - Feed Post Landscape (1200×630)' },
  { value: 'facebook-post-square', label: '👥 Facebook - Feed Post Square (1080×1080)' },
  { value: 'facebook-story', label: '👥 Facebook - Story (1080×1920)' },
  { value: 'facebook-reel', label: '👥 Facebook - Reel (1080×1920)' },
  { value: 'facebook-event-cover', label: '👥 Facebook - Event Cover (1200×628)' },
  
  // YouTube
  { value: 'youtube-banner', label: '🎥 YouTube - Channel Banner (2560×1440)' },
  { value: 'youtube-thumbnail', label: '🎥 YouTube - Video Thumbnail (1280×720)' },
  { value: 'youtube-short', label: '🎥 YouTube - Short (1080×1920)' },
  
  // LinkedIn
  { value: 'linkedin-banner-personal', label: '💼 LinkedIn - Personal Banner (1584×396)' },
  { value: 'linkedin-cover-company', label: '💼 LinkedIn - Company Cover (1128×191)' },
  { value: 'linkedin-post-square', label: '💼 LinkedIn - Feed Square (1200×1200)' },
  { value: 'linkedin-post-landscape', label: '💼 LinkedIn - Feed Landscape (1200×627)' },
];

const STYLES = [
  { value: 'clean', label: 'Clean - Minimal & Professional' },
  { value: 'bold', label: 'Bold - High Contrast & Impactful' },
  { value: 'premium', label: 'Premium - Elegant & Sophisticated' },
];

const CONTENT_TEMPLATES = [
  {
    label: '🎯 Promotional Offer',
    content: '<h2><strong>50% Off This Weekend Only!</strong></h2><p>Why choose us:</p><ul><li>Expert-led courses</li><li>Lifetime access</li><li>Certificate included</li></ul><p>Limited seats available!</p>'
  },
  {
    label: '📚 Course Launch',
    content: '<h2><strong>New Course: AI Mastery</strong></h2><ul><li>Learn from industry experts</li><li>Hands-on projects</li><li>Job placement support</li></ul><p>Enroll now and transform your career!</p>'
  },
  {
    label: '💼 Professional Service',
    content: '<h2><strong>Transform Your Career Today</strong></h2><p>Our services include:</p><ul><li>1-on-1 mentorship</li><li>Resume building</li><li>Interview prep</li></ul>'
  },
  {
    label: '🏆 Achievement',
    content: '<h2><strong>Join 10,000+ Happy Students</strong></h2><ul><li>95% pass rate</li><li>Global recognition</li><li>24/7 support</li></ul><p>Start your journey today!</p>'
  }
];

const CTA_EXAMPLES = ['Shop Now', 'Learn More', 'Get Started', 'Book Free Demo', 'Download Now'];

// Progress stages for creative generation
const GENERATION_STAGES = [
  { percent: 10, message: '🎨 Analyzing your content...', duration: 2000 },
  { percent: 25, message: '🎯 Planning creative strategy...', duration: 3000 },
  { percent: 40, message: '🖼️ Generating HD background...', duration: 5000 },
  { percent: 60, message: '✨ Adding design elements...', duration: 4000 },
  { percent: 75, message: '🔍 Quality control check...', duration: 3000 },
  { percent: 90, message: '📐 Final composition...', duration: 2000 },
  { percent: 95, message: '🚀 Almost ready...', duration: 1500 },
];

const SocialMediaCreativesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState('');
  const [style, setStyle] = useState('clean');
  const [content, setContent] = useState('');
  const [cta, setCta] = useState('');
  const [audience, setAudience] = useState('');
  const [context, setContext] = useState('');  // Context box
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  
  // Hero Images state (up to 3)
  const [heroImages, setHeroImages] = useState([]);
  const [heroImagePreviews, setHeroImagePreviews] = useState([]);
  
  // Progress state
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressStageIndex, setProgressStageIndex] = useState(0);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'image/png') {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    } else {
      toast.error('Please upload a PNG file');
    }
  };

  // Handle hero image upload (up to 3 images)
  const handleHeroImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    
    const validFiles = files.filter(file => validTypes.includes(file.type));
    if (validFiles.length !== files.length) {
      toast.error('Only JPG, PNG, and WebP images are allowed');
    }
    
    // Limit to 3 total
    const remainingSlots = 3 - heroImages.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);
    
    if (filesToAdd.length > 0) {
      setHeroImages(prev => [...prev, ...filesToAdd]);
      const newPreviews = filesToAdd.map(file => URL.createObjectURL(file));
      setHeroImagePreviews(prev => [...prev, ...newPreviews]);
    }
    
    if (validFiles.length > remainingSlots) {
      toast.info(`Maximum 3 hero images allowed. Added ${filesToAdd.length} image(s).`);
    }
  };

  // Remove hero image
  const removeHeroImage = (index) => {
    setHeroImages(prev => prev.filter((_, i) => i !== index));
    setHeroImagePreviews(prev => {
      URL.revokeObjectURL(prev[index]); // Clean up URL
      return prev.filter((_, i) => i !== index);
    });
  };

  // Progress simulation effect
  useEffect(() => {
    let timer;
    if (loading && progressStageIndex < GENERATION_STAGES.length) {
      const stage = GENERATION_STAGES[progressStageIndex];
      setProgress(stage.percent);
      setProgressMessage(stage.message);
      
      timer = setTimeout(() => {
        setProgressStageIndex(prev => prev + 1);
      }, stage.duration);
    }
    return () => clearTimeout(timer);
  }, [loading, progressStageIndex]);

  const handleGenerate = async () => {
    // Parse the rich text content
    const parsedContent = parseRichTextContent(content);
    
    if (!platform || !parsedContent.plainText) {
      toast.error('Please select platform and enter content');
      return;
    }

    setLoading(true);
    setGeneratedImage(null);
    
    // Reset progress
    setProgress(0);
    setProgressMessage('🚀 Starting creative generation...');
    setProgressStageIndex(0);

    try {
      const formData = new FormData();
      formData.append('platform', platform);
      formData.append('style', style);
      // Send structured content as JSON
      formData.append('content_data', JSON.stringify({
        headline: parsedContent.headline,
        bullets: parsedContent.bullets,
        body: parsedContent.body,
        rawHtml: content
      }));
      // Also send headline for backward compatibility
      formData.append('headline', parsedContent.headline || parsedContent.plainText.substring(0, 100));
      formData.append('cta', cta);
      formData.append('audience', audience);
      formData.append('context', context);
      if (logo) formData.append('logo', logo);
      
      // Append hero images (up to 3)
      if (heroImages.length > 0) {
        heroImages.forEach((img, index) => {
          formData.append(`hero_image_${index + 1}`, img);
        });
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/creator-ai/social-creatives`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Complete the progress
      setProgress(100);
      setProgressMessage('✅ Creative ready!');

      if (response.data.success) {
        setGeneratedImage(response.data.image_url);
        const creditsUsed = response.data.credits_used || 0;
        const creditsRemaining = response.data.credits_remaining;
        if (creditsUsed > 0) {
          toast.success(`Creative generated! Used ${creditsUsed} credits. Remaining: ${creditsRemaining}`);
        } else {
          toast.success('Creative generated successfully!');
        }
      }
    } catch (error) {
      console.error('Generation error:', error);
      setProgress(0);
      setProgressMessage('');
      if (error.response?.status === 402) {
        toast.error(error.response?.data?.detail || 'Insufficient credits. Please purchase more credits.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate creative');
      }
    } finally {
      setLoading(false);
      // Reset progress after a delay
      setTimeout(() => {
        setProgress(0);
        setProgressMessage('');
        setProgressStageIndex(0);
      }, 2000);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `creative-${platform}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Downloaded!');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          <button
            onClick={() => navigate('/image-generator')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Creator AI</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <ImageIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Social Media Creatives</h1>
              <p className="text-xs sm:text-sm text-gray-500">Create high-impact static creatives for any platform</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Inputs */}
          <Card className="border-2 border-purple-200">
            <CardContent className="p-4 sm:p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Creative Settings</h2>
              
              <div className="space-y-4">
                <div>
                  <Label>Platform & Placement *</Label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Style Preset</Label>
                  <Select value={style} onValueChange={setStyle}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STYLES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Type className="h-4 w-4" />
                    Content *
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Add your headline, bullet points, and body text. Use the toolbar to format.
                  </p>
                  <SimpleRichTextEditor
                    value={content}
                    onChange={setContent}
                    placeholder="Write your content here... Use headings for main text, bullet points for features, and paragraphs for descriptions."
                    minHeight="120px"
                  />
                  <div className="mt-3">
                    <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                      <Info className="h-3 w-3" /> Quick templates (click to use):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {CONTENT_TEMPLATES.map((template, i) => (
                        <button
                          key={i}
                          onClick={() => setContent(template.content)}
                          className="text-xs px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100 transition-colors border border-purple-200"
                        >
                          {template.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Call to Action (optional)</Label>
                  <Input
                    value={cta}
                    onChange={(e) => setCta(e.target.value)}
                    placeholder="e.g., Learn More, Shop Now"
                    maxLength={30}
                    className="mt-1"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {CTA_EXAMPLES.map((ex, i) => (
                      <button
                        key={i}
                        onClick={() => setCta(ex)}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Target Audience (optional)</Label>
                  <Input
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    placeholder="e.g., Students, Professionals, Doctors"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Creative Context (optional)
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Describe the purpose and target to help AI create more relevant designs
                  </p>
                  <textarea
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="Example: This is for a flash sale targeting young professionals. We want to create urgency and excitement. Show a group of happy customers celebrating their purchase."
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px] resize-y"
                    rows={3}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    💡 Tip: Mention if you want a group of people, single person, two people, specific setting, mood, or style
                  </p>
                </div>

                <div>
                  <Label>Brand Logo (PNG)</Label>
                  <div className="mt-1">
                    {logoPreview ? (
                      <div className="relative inline-block">
                        <img src={logoPreview} alt="Logo" className="h-16 object-contain border rounded p-2" loading="lazy" decoding="async"/>
                        <button
                          onClick={() => { setLogo(null); setLogoPreview(null); }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer hover:border-purple-400 transition-colors">
                        <Upload className="h-5 w-5 text-gray-400" />
                        <span className="text-sm text-gray-500">Upload PNG logo</span>
                        <input type="file" accept=".png" onChange={handleLogoUpload} className="hidden" />
                      </label>
                    )}
                    <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                      <Info className="h-3 w-3" /> Recommended: 200×200px to 500×500px, transparent background
                    </p>
                  </div>
                </div>

                {/* Hero Images Upload Section */}
                <div className="border-t border-gray-100 pt-4">
                  <Label className="flex items-center gap-2">
                    <span>🖼️ Hero Images</span>
                    <span className="text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">Optional</span>
                  </Label>
                  <p className="text-xs text-gray-500 mb-2 mt-1">
                    Upload product photos, person images, or property photos to feature in your creative. AI will integrate them professionally.
                  </p>
                  
                  {/* Hero Images Preview Grid */}
                  {heroImagePreviews.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {heroImagePreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={preview} 
                            alt={`Hero ${index + 1}`} 
                            className="h-20 w-20 object-cover rounded-lg border-2 border-purple-200" loading="lazy" decoding="async"/>
                          <button
                            onClick={() => removeHeroImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                          <span className="absolute bottom-1 left-1 text-xs bg-black/60 text-white px-1 rounded">
                            {index + 1}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload Button - Show only if less than 3 images */}
                  {heroImages.length < 3 && (
                    <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-purple-200 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-all">
                      <Upload className="h-5 w-5 text-purple-400" />
                      <span className="text-sm text-purple-600">
                        {heroImages.length === 0 ? 'Upload hero images (up to 3)' : `Add more (${3 - heroImages.length} remaining)`}
                      </span>
                      <input 
                        type="file" 
                        accept="image/jpeg,image/png,image/webp,image/jpg" 
                        multiple
                        onChange={handleHeroImageUpload} 
                        className="hidden" 
                      />
                    </label>
                  )}
                  
                  <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                    <Info className="h-3 w-3" /> Supports: JPG, PNG, WebP • Max 3 images • Product, person, or property photos
                  </p>
                  
                  {heroImages.length > 0 && (
                    <div className="mt-2 p-2 bg-purple-50 rounded-lg text-xs text-purple-700">
                      <strong>📸 Cinematographic AI:</strong> Your hero images will be integrated professionally with studio-quality lighting, 
                      beautiful depth of field, and premium commercial photography aesthetics.
                    </div>
                  )}
                </div>

                {/* Progress Bar - Only show when loading */}
                {loading && (
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-purple-700 font-medium">{progressMessage}</span>
                      <span className="text-purple-600 font-bold">{progress}%</span>
                    </div>
                    <div className="w-full bg-purple-100 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-purple-500 via-pink-500 to-purple-600 h-3 rounded-full transition-all duration-500 ease-out relative"
                        style={{ width: `${progress}%` }}
                      >
                        <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Creating your professional creative (typically 15-30 seconds)
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleGenerate}
                  disabled={loading || !platform || !content}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 py-3"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating... {progress}%</>
                  ) : (
                    <><ImageIcon className="h-4 w-4 mr-2" /> Generate Creative</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Preview */}
          <Card className="border-2 border-gray-200">
            <CardContent className="p-4 sm:p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Preview & Download</h2>
              
              <div className="aspect-square bg-gray-100 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300 overflow-hidden">
                {generatedImage ? (
                  <img src={generatedImage} alt="Generated" className="max-w-full max-h-full object-contain" loading="lazy" decoding="async"/>
                ) : (
                  <div className="text-center text-gray-400 p-8">
                    <ImageIcon className="h-16 w-16 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Your creative will appear here</p>
                    <p className="text-xs mt-1">Fill in the settings and click Generate</p>
                  </div>
                )}
              </div>
              
              {generatedImage && (
                <Button 
                  onClick={handleDownload} 
                  className="w-full mt-4 bg-green-600 hover:bg-green-700"
                >
                  <Download className="h-4 w-4 mr-2" /> Download Creative
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="mb-20 sm:mb-24"></div>
      <Footer />
    </div>
  );
};

export default SocialMediaCreativesPage;